import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase, Site, Worker, Induction, ProjectStatus, Hygienist } from '../lib/supabase';
import { SiteForm } from '../components/forms/SiteForm';
import { uploadFile } from '../utils/fileUpload';
import { formatDisplayDate } from '../utils/dateUtils';
import { Building2, MapPin, Calendar, Users, ClipboardCheck, CreditCard as Edit, ArrowLeft, AlertTriangle, CheckCircle, Clock, FileText, Shield, Zap, Flame, Building, Fence, Wind, TestTube, Phone, Calculator, User, Plus, Save, X, UserPlus, Check, Upload, Download, Droplet, Droplets, Timer } from 'lucide-react';

interface SiteWithDetails extends Site {
  workers?: Worker[];
  inductions?: (Induction & { worker?: Worker })[];
}

export const SiteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteWithDetails | null>(null);
  const [projectStatus, setProjectStatus] = useState<ProjectStatus | null>(null);
  const [hygienists, setHygienists] = useState<Hygienist[]>([]);
  const [availableWorkers, setAvailableWorkers] = useState<Worker[]>([]);
  const [showAssignWorkers, setShowAssignWorkers] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState<string[]>([]);
  const [assigningWorkers, setAssigningWorkers] = useState(false);
  const [showAddHygienist, setShowAddHygienist] = useState(false);
  const [newHygienist, setNewHygienist] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [defaultStatus] = useState<Omit<ProjectStatus, 'id' | 'site_id' | 'created_at' | 'updated_at'>>({
    gas_approval: 'live',
    electrical_approval: 'live',
    water_approval: 'live',
    sewer_approval: 'live',
    council_approval: 'pending',
    epa_permit: 'not_required',
    safework_notification: 'pending',
    temporary_fence: 'pending',
    air_monitoring: 'not_required',
    soil_testing: 'not_required',
    hygienist_required: false,
    gas_approval_notes: '',
    electrical_approval_notes: '',
    water_approval_notes: '',
    sewer_approval_notes: '',
    council_approval_notes: '',
    epa_permit_notes: '',
    safework_notification_notes: '',
    temporary_fence_notes: '',
    air_monitoring_notes: '',
    soil_testing_notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [globalUnit, setGlobalUnit] = useState<'tonnes' | 'cubic_metres'>('tonnes');
  const [wasteEstimates, setWasteEstimates] = useState<Record<string, { amount: string; unit: string }>>({});
  const [loadingWasteData, setLoadingWasteData] = useState(true);
  const [editingWasteEstimates, setEditingWasteEstimates] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [tempStatus, setTempStatus] = useState<Partial<ProjectStatus>>({});
  const [statusSaving, setStatusSaving] = useState(false);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [processingSpreadsheet, setProcessingSpreadsheet] = useState(false);
  const [siteTimesheetData, setSiteTimesheetData] = useState<{
    totalHours: number;
    totalPay: number;
    totalRegularHours: number;
    totalOvertimeHours: number;
    timesheetCount: number;
  } | null>(null);
  const [loadingTimesheetData, setLoadingTimesheetData] = useState(true);

  useEffect(() => {
    if (id) {
      fetchSiteDetails();
      fetchWasteQuantities();
      fetchHygienists();
      fetchAvailableWorkers();
      fetchSiteTimesheetData();
    }
  }, [id]);

  const fetchHygienists = async () => {
    try {
      const { data, error } = await supabase
        .from('hygienists')
        .select('*')
        .order('name');

      if (error) throw error;
      setHygienists(data || []);
    } catch (error) {
      console.error('Error fetching hygienists:', error);
    }
  };

  const fetchAvailableWorkers = async () => {
    try {
      // Get all active workers
      const { data: allWorkers, error: workersError } = await supabase
        .from('workers')
        .select('*')
        .eq('status', 'active');

      if (workersError) throw workersError;

      // Get workers who already have inductions for this site
      const { data: workersWithInductions, error: inductionsError } = await supabase
        .from('inductions')
        .select('worker_id')
        .eq('site_id', id)
        .not('worker_id', 'is', null);

      if (inductionsError) throw inductionsError;

      const workerIdsWithInductions = new Set(workersWithInductions?.map(i => i.worker_id) || []);
      const available = allWorkers?.filter(worker => !workerIdsWithInductions.has(worker.id)) || [];

      setAvailableWorkers(available);
    } catch (error) {
      console.error('Error fetching available workers:', error);
    }
  };

  const fetchSiteTimesheetData = async () => {
    try {
      setLoadingTimesheetData(true);
      const { data: timesheets, error } = await supabase
        .from('timesheets')
        .select(`
          total_hours,
          total_pay,
          regular_hours,
          overtime_hours,
          worker_id,
          workers!inner(hourly_pay_rate, overtime_pay_rate),
          breaks(*)
        `)
        .eq('site_id', id);

      if (error) throw error;

      // Calculate net hours (gross hours minus breaks) - this matches the Timesheets page calculation
      const totalHours = timesheets?.reduce((sum, ts) => {
        const grossHours = ts.total_hours || 0;
        const breakMinutes = (ts.breaks || []).reduce((breakSum: number, b: any) => breakSum + (b.duration_minutes || 0), 0);
        const netHours = Math.max(0, grossHours - (breakMinutes / 60));
        return sum + netHours;
      }, 0) || 0;

      const totalRegularHours = timesheets?.reduce((sum, ts) => sum + (ts.regular_hours || 0), 0) || 0;
      const totalOvertimeHours = timesheets?.reduce((sum, ts) => sum + (ts.overtime_hours || 0), 0) || 0;

      // Calculate total pay using current worker wage rates
      const totalPay = timesheets?.reduce((sum, ts) => {
        const worker = ts.workers as any;
        const hourlyRate = worker?.hourly_pay_rate || 0;
        const overtimeRate = worker?.overtime_pay_rate || (hourlyRate * 1.5);
        const regularHours = ts.regular_hours || (ts.total_hours && ts.total_hours <= 8 ? ts.total_hours : 8);
        const overtimeHours = ts.overtime_hours || (ts.total_hours && ts.total_hours > 8 ? ts.total_hours - 8 : 0);

        const pay = (regularHours * hourlyRate) + (overtimeHours * overtimeRate);
        return sum + pay;
      }, 0) || 0;

      setSiteTimesheetData({
        totalHours,
        totalPay,
        totalRegularHours,
        totalOvertimeHours,
        timesheetCount: timesheets?.length || 0
      });
    } catch (error) {
      console.error('Error fetching site timesheet data:', error);
    } finally {
      setLoadingTimesheetData(false);
    }
  };

  const handleAssignWorkers = async () => {
    if (selectedWorkers.length === 0) return;
    
    setAssigningWorkers(true);
    try {
      // Create pending inductions for selected workers
      const newInductions = selectedWorkers.map(workerId => ({
        site_id: id,
        worker_id: workerId,
        status: 'pending'
      }));

      const { error } = await supabase
        .from('inductions')
        .insert(newInductions);

      if (error) throw error;

      // Refresh site details and available workers
      await Promise.all([
        fetchSiteDetails(),
        fetchAvailableWorkers()
      ]);
      
      setShowAssignWorkers(false);
      setSelectedWorkers([]);
    } catch (error) {
      console.error('Error creating inductions for workers:', error);
      alert('Failed to create inductions for workers. Please try again.');
    } finally {
      setAssigningWorkers(false);
    }
  };

  const handleRemoveWorker = async (workerId: string) => {
    if (!confirm('Are you sure you want to remove this worker? This will delete their induction record.')) {
      return;
    }

    try {
      // Delete the worker's induction for this site
      const { error } = await supabase
        .from('inductions')
        .delete()
        .eq('site_id', id)
        .eq('worker_id', workerId);

      if (error) throw error;

      // Refresh site details and available workers
      await Promise.all([
        fetchSiteDetails(),
        fetchAvailableWorkers()
      ]);
    } catch (error) {
      console.error('Error removing worker induction:', error);
      alert('Failed to remove worker induction. Please try again.');
    }
  };

  const toggleWorkerSelection = (workerId: string) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleAddHygienist = async () => {
    if (!newHygienist.name.trim() || !newHygienist.phone.trim()) {
      alert('Name and phone number are required');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('hygienists')
        .insert([newHygienist])
        .select()
        .single();

      if (error) throw error;

      setHygienists(prev => [...prev, data]);
      setNewHygienist({ name: '', phone: '', email: '', company: '' });
      setShowAddHygienist(false);
      
      // Auto-select the newly added hygienist
      setTempStatus(prev => ({
        ...prev,
        hygienist_id: data.id,
        hygienist_name: data.name,
        hygienist_phone: data.phone
      }));
    } catch (error) {
      console.error('Error adding hygienist:', error);
      alert('Failed to add hygienist. Please try again.');
    }
  };

  const fetchSiteDetails = async () => {
    try {
      // Fetch site details
      const { data: siteData, error: siteError } = await supabase
        .from('sites')
        .select('*')
        .eq('id', id)
        .single();

      if (siteError) throw siteError;

      // Fetch workers who have inductions for this site
      const { data: inductionsData, error: inductionsError } = await supabase
        .from('inductions')
        .select(`
          *,
          workers!inner(id, name, role, email, phone, certifications, status)
        `)
        .eq('site_id', id)
        .order('created_at', { ascending: false });

      if (inductionsError) throw inductionsError;

      // Extract unique workers from inductions
      const workersMap = new Map();
      const inductions = inductionsData?.map(induction => {
        const worker = induction.workers;
        if (worker && !workersMap.has(worker.id)) {
          workersMap.set(worker.id, worker);
        }
        return {
          ...induction,
          worker: worker
        };
      }) || [];

      const workers = Array.from(workersMap.values());

      setSite({
        ...siteData,
        workers,
        inductions
      });

      // Fetch project status
      const { data: statusData, error: statusError } = await supabase
        .from('project_status')
        .select('*')
        .eq('site_id', id)
        .maybeSingle();

      if (statusError) {
        console.error('Error fetching project status:', statusError);
        setProjectStatus(null);
      } else if (statusData) {
        setProjectStatus(statusData);
      } else {
        // Create default project status if none exists
        const newStatus = {
          site_id: id,
          ...defaultStatus,
          epa_permit: siteData.type === 'asbestos_removal' ? 'pending' : 'not_required',
          air_monitoring: siteData.type === 'asbestos_removal' ? 'pending' : 'not_required',
          hygienist_required: siteData.type === 'asbestos_removal',
          hygienist_name: siteData.type === 'asbestos_removal' ? 'Dr. Sarah Johnson' : undefined,
          hygienist_phone: siteData.type === 'asbestos_removal' ? '+61 2 9876 5432' : undefined,
        };

        try {
          const { data: createdStatus, error: createError } = await supabase
            .from('project_status')
            .insert([newStatus])
            .select()
            .single();

          if (createError) {
            // If duplicate key error, try to fetch the existing record
            if (createError.code === '23505') {
              const { data: existingStatus, error: fetchError } = await supabase
                .from('project_status')
                .select('*')
                .eq('site_id', id)
                .single();
              
              if (fetchError) {
                console.error('Error fetching existing project status:', fetchError);
                setProjectStatus(null);
              } else {
                setProjectStatus(existingStatus);
              }
            } else {
              console.error('Error creating project status:', createError);
              setProjectStatus(null);
            }
          } else {
            setProjectStatus(createdStatus);
          }
        } catch (error) {
          console.error('Unexpected error with project status:', error);
          setProjectStatus(null);
        }
      }
    } catch (error) {
      console.error('Error fetching site details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWasteQuantities = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('waste_quantities')
        .select('*')
        .eq('site_id', id);

      if (error) throw error;

      // Convert database records to the format expected by the component
      const wasteData: Record<string, { amount: string; unit: string }> = {};
      
      // Initialize with default waste types
      const defaultWasteTypes = [
        'asbestos', 'demolition_waste', 'bricks_tile', 'concrete',
        'soil', 'green_waste', 'steel', 'timber'
      ];
      
      defaultWasteTypes.forEach(type => {
        wasteData[type] = { amount: '0', unit: 'tonnes' };
      });
      
      // Update with actual data from database
      data?.forEach(record => {
        wasteData[record.waste_type] = {
          amount: record.amount?.toString() || '0',
          unit: record.unit || 'tonnes'
        };
      });
      
      setWasteEstimates(wasteData);
    } catch (error) {
      console.error('Error fetching waste quantities:', error);
      // Initialize with default values on error
      const defaultWasteData: Record<string, { amount: string; unit: string }> = {};
      const defaultWasteTypes = [
        'asbestos', 'demolition_waste', 'bricks_tile', 'concrete',
        'soil', 'green_waste', 'steel', 'timber'
      ];
      
      defaultWasteTypes.forEach(type => {
        defaultWasteData[type] = { amount: '0', unit: 'tonnes' };
      });
      
      setWasteEstimates(defaultWasteData);
    } finally {
      setLoadingWasteData(false);
    }
  };

  const handleUpdateSite = async (siteData: Partial<Site>, projectStatusData?: any) => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('sites')
        .update(siteData)
        .eq('id', id);
      
      if (error) throw error;
      
      // Update project status if provided
      if (projectStatusData && id) {
        const { error: statusError } = await supabase
          .from('project_status')
          .upsert([{
            site_id: id,
            ...projectStatusData
          }], { onConflict: 'site_id' });
        
        if (statusError) {
          console.error('Error updating project status:', statusError);
          throw statusError;
        }
      }
      
      setShowEditModal(false);
      fetchSiteDetails();
    } catch (error) {
      console.error('Error updating site:', error);
      alert('Failed to update site. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWasteEstimateChange = async (wasteType: string, field: 'amount' | 'unit', value: string) => {
    // Update local state immediately for responsive UI
    setWasteEstimates(prev => ({
      ...prev,
      [wasteType]: {
        ...prev[wasteType as keyof typeof prev],
        [field]: value
      }
    }));
    
    // Save to database
    if (id) {
      try {
        const currentEstimate = wasteEstimates[wasteType];
        const updatedEstimate = {
          ...currentEstimate,
          [field]: value
        };
        
        await supabase
          .from('waste_quantities')
          .upsert({
            site_id: id,
            waste_type: wasteType,
            amount: parseFloat(updatedEstimate.amount) || 0,
            unit: updatedEstimate.unit
          }, {
            onConflict: 'site_id,waste_type'
          });
      } catch (error) {
        console.error('Error saving waste estimate:', error);
      }
    }
  };

  const handleGlobalUnitChange = async (newUnit: 'tonnes' | 'cubic_metres') => {
    setGlobalUnit(newUnit);
    
    // Update all waste estimates to use the new global unit
    const updatedEstimates = { ...wasteEstimates };
    const updatePromises: Promise<any>[] = [];
    
    Object.keys(updatedEstimates).forEach(wasteType => {
      updatedEstimates[wasteType] = {
        ...updatedEstimates[wasteType],
        unit: newUnit
      };
      
      // Save each change to database
      if (id) {
        const promise = supabase
          .from('waste_quantities')
          .upsert({
            site_id: id,
            waste_type: wasteType,
            amount: parseFloat(updatedEstimates[wasteType].amount) || 0,
            unit: newUnit
          }, {
            onConflict: 'site_id,waste_type'
          });
        updatePromises.push(promise);
      }
    });
    
    // Update local state immediately for responsive UI
    setWasteEstimates(updatedEstimates);
    
    // Wait for all database updates to complete
    try {
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating waste estimate units:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'disconnected':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'approved':
      case 'completed':
      case 'submitted':
      case 'installed':
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'cyclone_fence':
        return <CheckCircle className="h-5 w-5 text-blue-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'rejected':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'not_required':
      case 'not_applicable':
        return <div className="h-5 w-5 bg-gray-300 rounded-full flex items-center justify-center">
          <span className="text-xs text-gray-600">N/A</span>
        </div>;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getApprovalStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-red-100 text-red-800';
      case 'disconnected':
        return 'bg-green-100 text-green-800';
      case 'approved':
      case 'completed':
      case 'submitted':
      case 'installed':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'cyclone_fence':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'required':
        return 'bg-red-100 text-red-800';
      case 'not_required':
      case 'not_applicable':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusChange = (field: keyof ProjectStatus, value: string) => {
    setTempStatus(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHygienistChange = (hygienistId: string) => {
    const selectedHygienist = hygienists.find(h => h.id === hygienistId);
    if (selectedHygienist) {
      setTempStatus(prev => ({
        ...prev,
        hygienist_id: hygienistId,
        hygienist_name: selectedHygienist.name,
        hygienist_phone: selectedHygienist.phone
      }));
    }
  };

  const handleSaveStatus = async () => {
    if (!projectStatus || !id) return;
    
    setStatusSaving(true);
    try {
      // Separate site updates from project status updates
      const siteUpdates: any = {};
      const statusUpdates: any = {};
      
      // Check if asbestos_type needs to be updated in sites table
      if (tempStatus.asbestos_type !== undefined) {
        siteUpdates.asbestos_type = tempStatus.asbestos_type;
      }
      
      // All other fields go to project_status table
      Object.keys(tempStatus).forEach(key => {
        if (key !== 'asbestos_type') {
          statusUpdates[key] = tempStatus[key];
        }
      });
      
      // Update sites table if needed
      if (Object.keys(siteUpdates).length > 0) {
        const { error: siteError } = await supabase
          .from('sites')
          .update(siteUpdates)
          .eq('id', id);
        
        if (siteError) throw siteError;
        
        // Update local site state
        setSite(prev => prev ? { ...prev, ...siteUpdates } : null);
      }
      
      // Update project_status table if needed
      if (Object.keys(statusUpdates).length > 0) {
      const { error } = await supabase
        .from('project_status')
        .upsert([{
          site_id: id,
            ...statusUpdates
        }], { onConflict: 'site_id' })
        .eq('site_id', id);
      
      if (error) throw error;
      
      // Update local state
        setProjectStatus(prev => prev ? { ...prev, ...statusUpdates } : null);
      }
      
      setEditingStatus(false);
      setTempStatus({});
    } catch (error) {
      console.error('Error saving project status:', error);
      alert('Failed to save status changes. Please try again.');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setTempStatus({});
    setEditingStatus(false);
  };

  const renderStatusControl = (
    field: keyof ProjectStatus,
    label: string,
    icon: React.ReactNode,
    options: { value: string; label: string }[]
  ) => {
    if (!projectStatus) return null;

    const currentValue = editingStatus && tempStatus[field] !== undefined
      ? tempStatus[field]
      : projectStatus[field];

    const notesField = `${field}_notes` as keyof ProjectStatus;
    const currentNotes = editingStatus && tempStatus[notesField] !== undefined
      ? tempStatus[notesField]
      : projectStatus[notesField];

    // Find the label for the current value
    const currentOption = options.find(opt => opt.value === currentValue);
    const displayLabel = currentOption?.label || (currentValue as string).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            {icon}
            <span className="text-sm font-medium">{label}</span>
          </div>
          <div className="flex items-center space-x-2">
            {editingStatus ? (
              <select
                value={currentValue as string}
                onChange={(e) => handleStatusChange(field, e.target.value)}
                className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <>
                {getApprovalStatusIcon(currentValue as string)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getApprovalStatusColor(currentValue as string)}`}>
                  {displayLabel}
                </span>
              </>
            )}
          </div>
        </div>

        {editingStatus ? (
          <textarea
            value={currentNotes as string || ''}
            onChange={(e) => handleStatusChange(notesField, e.target.value)}
            placeholder="Add notes..."
            className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            rows={2}
          />
        ) : currentNotes ? (
          <div className="px-3 py-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 whitespace-pre-wrap">{currentNotes as string}</p>
          </div>
        ) : null}
      </div>
    );
  };

  const handleQuoteUpload = async (file: File) => {
    if (!file || !site) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'text/csv', // .csv
      'application/pdf' // .pdf
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload an Excel file (.xls, .xlsx), CSV file, or PDF');
      return;
    }

    setUploadingQuote(true);
    try {
      const result = await uploadFile(file, 'quote', 'site-quotes');
      
      if (result.success && result.url) {
        // Update the site record with the new quote URL
        const { error } = await supabase
          .from('sites')
          .update({ quote_url: result.url })
          .eq('id', site.id);

        if (error) throw error;

        // Refresh site data
        fetchSiteDetails();
        alert('Quote file uploaded successfully!');
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading quote file:', error);
      alert(`Failed to upload quote file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingQuote(false);
    }
  };

  const handleQuoteFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleQuoteUpload(e.target.files[0]);
    }
  };

  const removeQuoteFile = async () => {
    if (!site) return;

    try {
      const { error } = await supabase
        .from('sites')
        .update({ quote_url: null })
        .eq('id', site.id);

      if (error) throw error;

      fetchSiteDetails();
    } catch (error) {
      console.error('Error removing quote file:', error);
      alert('Failed to remove quote file');
    }
  };

  const getInductionStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'expired':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      default:
        return <ClipboardCheck className="h-5 w-5 text-gray-600" />;
    }
  };

  const processSpreadsheet = async () => {
    if (!site?.quote_url) return;
    
    setProcessingSpreadsheet(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('process-excel-quote', {
        body: {
          site_id: site.id,
          quote_url: site.quote_url
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to process spreadsheet');
      }

      if (data?.success) {
        alert(`Success! Processed ${data.processed_quantities} waste quantities from spreadsheet.`);
        // Refresh waste quantities to show the updated data
        fetchWasteQuantities();
      } else {
        throw new Error(data?.error || 'Unknown error processing spreadsheet');
      }
    } catch (error) {
      console.error('Error processing spreadsheet:', error);
      alert(`Failed to process spreadsheet: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingSpreadsheet(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Site not found</h3>
        <Link to="/sites" className="text-orange-600 hover:text-orange-700">
          Return to Sites
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/sites')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{site.name}</h1>
            <p className="text-gray-600">{site.address}</p>
          </div>
        </div>
        <button
          onClick={() => setShowEditModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Edit className="h-5 w-5" />
          <span>Edit Site</span>
        </button>
      </div>

      {/* Site Status Banner */}
      <div className={`rounded-lg p-6 ${
        site.status === 'active' ? 'bg-green-50 border border-green-200' :
        site.status === 'completed' ? 'bg-blue-50 border border-blue-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
              site.status === 'active' ? 'bg-green-100' :
              site.status === 'completed' ? 'bg-blue-100' :
              'bg-red-100'
            }`}>
              <Building2 className={`h-6 w-6 ${
                site.status === 'active' ? 'text-green-600' :
                site.status === 'completed' ? 'text-blue-600' :
                'text-red-600'
              }`} />
            </div>
            <div>
              <h3 className={`font-medium text-lg ${
                site.status === 'active' ? 'text-green-900' :
                site.status === 'completed' ? 'text-blue-900' :
                'text-red-900'
              }`}>
                Project Status: {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
              </h3>
              <p className={`text-sm ${
                site.status === 'active' ? 'text-green-700' :
                site.status === 'completed' ? 'text-blue-700' :
                'text-red-700'
              }`}>
                {site.type.replace('_', ' ').charAt(0).toUpperCase() + site.type.replace('_', ' ').slice(1)} Project
              </p>
            </div>
          </div>
          <div className="text-right space-y-1">
            <p className={`text-sm font-medium ${
              site.status === 'active' ? 'text-green-800' :
              site.status === 'completed' ? 'text-blue-800' :
              'text-red-800'
            }`}>
              {new Date(site.start_date).toLocaleDateString()} - {new Date(site.end_date).toLocaleDateString()}
            </p>
            <p className={`text-xs ${
              site.status === 'active' ? 'text-green-600' :
              site.status === 'completed' ? 'text-blue-600' :
              'text-red-600'
            }`}>
              Duration: {Math.ceil((new Date(site.end_date).getTime() - new Date(site.start_date).getTime()) / (1000 * 60 * 60 * 24))} days
            </p>
            {site.safework_expiry_date && (
              <p className={`text-xs font-medium flex items-center justify-end space-x-1 ${
                new Date(site.safework_expiry_date) < new Date()
                  ? 'text-red-600'
                  : site.status === 'active' ? 'text-green-700' :
                    site.status === 'completed' ? 'text-blue-700' :
                    'text-red-700'
              }`}>
                <Calendar className="h-3 w-3" />
                <span>SafeWork Expires: {formatDisplayDate(site.safework_expiry_date)}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Site Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Site Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Type</label>
                <p className="text-sm text-gray-900 capitalize">{site.type.replace('_', ' ')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(site.status)}`}>
                  {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <p className="text-sm text-gray-900">{new Date(site.start_date).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <p className="text-sm text-gray-900">{new Date(site.end_date).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asbestos Type</label>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  site.asbestos_type === 'friable' ? 'bg-red-100 text-red-800' :
                  site.asbestos_type === 'non_friable' ? 'text-white' :
                  site.asbestos_type === 'mixed' ? 'bg-blue-100 text-blue-800' :
                  site.asbestos_type === 'unknown' ? 'bg-purple-100 text-purple-800' :
                  'bg-green-100 text-green-800'
                }`} style={site.asbestos_type === 'non_friable' ? { backgroundColor: '#f59e0b' } : {}}>
                  {site.asbestos_type === 'non_friable' ? 'Non-Friable' :
                   site.asbestos_type === 'mixed' ? 'Mixed' :
                   site.asbestos_type === 'unknown' ? 'Unknown - Testing Required' :
                   site.asbestos_type?.charAt(0).toUpperCase() + site.asbestos_type?.slice(1) || 'None'}
                </span>
              </div>
            </div>
            {site.description && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-sm text-gray-900">{site.description}</p>
              </div>
            )}
            {site.requirements && site.requirements.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Safety Requirements</label>
                <div className="flex flex-wrap gap-2">
                  {site.requirements.map((req, index) => (
                    <span key={index} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Project Approvals & Utilities */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Project Approvals & Utilities</h3>
              <div className="flex items-center space-x-2">
                {site.quote_url && (
                  <button
                    onClick={processSpreadsheet}
                    disabled={processingSpreadsheet}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
                    title="Process uploaded spreadsheet to extract waste quantities"
                  >
                    {processingSpreadsheet ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-3 w-3" />
                        <span>Process Spreadsheet</span>
                      </>
                    )}
                  </button>
                )}
                {editingStatus ? (
                  <>
                    <button
                      onClick={handleSaveStatus}
                      disabled={statusSaving}
                      className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                    >
                      {statusSaving ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      <span>{statusSaving ? 'Saving...' : 'Save'}</span>
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={statusSaving}
                      className="flex items-center space-x-1 px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                    >
                      <X className="h-3 w-3" />
                      <span>Cancel</span>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setEditingStatus(true);
                      setTempStatus({});
                    }}
                    className="flex items-center space-x-1 px-3 py-1 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                  >
                    <Edit className="h-3 w-3" />
                    <span>Edit Status</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Utilities */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800 flex items-center">
                  <Zap className="h-4 w-4 mr-2 text-yellow-600" />
                  Utilities & Services
                </h4>
                
                <div className="space-y-3">
                  {renderStatusControl(
                    'gas_approval',
                    'Gas Status',
                    <Flame className="h-4 w-4 text-blue-600" />,
                    [
                      { value: 'live', label: 'Live' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'disconnected', label: 'Disconnected' },
                      { value: 'not_applicable', label: 'Not Applicable' }
                    ]
                  )}

                  {renderStatusControl(
                    'electrical_approval',
                    'Electrical Status',
                    <Zap className="h-4 w-4 text-yellow-600" />,
                    [
                      { value: 'live', label: 'Live' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'disconnected', label: 'Disconnected' },
                      { value: 'not_applicable', label: 'Not Applicable' }
                    ]
                  )}

                  {renderStatusControl(
                    'water_approval',
                    'Water Status',
                    <Droplet className="h-4 w-4 text-cyan-600" />,
                    [
                      { value: 'live', label: 'Live' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'disconnected', label: 'Disconnected' }
                    ]
                  )}

                  {renderStatusControl(
                    'sewer_approval',
                    'Sewer Status',
                    <Droplets className="h-4 w-4 text-teal-600" />,
                    [
                      { value: 'live', label: 'Live' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'disconnected', label: 'Disconnected' }
                    ]
                  )}
                </div>
              </div>

              {/* Approvals */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  Regulatory Approvals
                </h4>
                
                <div className="space-y-3">
                  {renderStatusControl(
                    'council_approval',
                    'Council Approval',
                    <Building className="h-4 w-4 text-purple-600" />,
                    [
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'not_applicable', label: 'Not Applicable' }
                    ]
                  )}

                  {site.type === 'asbestos_removal' && renderStatusControl(
                    'epa_permit',
                    'EPA Permit',
                    <Shield className="h-4 w-4 text-red-600" />,
                    [
                      { value: 'not_required', label: 'Not Required' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' }
                    ]
                  )}

                  {renderStatusControl(
                    'safework_notification',
                    'SafeWork Notification',
                    <FileText className="h-4 w-4 text-orange-600" />,
                    [
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' }
                    ]
                  )}

                  {site.safework_expiry_date && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-orange-600" />
                          <span className="text-sm font-medium text-gray-700">SafeWork Expiry Date</span>
                        </div>
                        <span className={`text-sm font-semibold ${
                          new Date(site.safework_expiry_date) < new Date()
                            ? 'text-red-600'
                            : 'text-gray-900'
                        }`}>
                          {formatDisplayDate(site.safework_expiry_date)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Safety & Environmental Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Safety & Environmental Controls</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Site Safety */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800 flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-green-600" />
                  Site Safety
                </h4>
                
                <div className="space-y-3">
                  {renderStatusControl(
                    'temporary_fence',
                    'Site Fencing',
                    <Fence className="h-4 w-4 text-gray-600" />,
                    [
                      { value: 'pending', label: 'Pending' },
                      { value: 'installed', label: 'Temp Fence' },
                      { value: 'cyclone_fence', label: 'Cyclone Fence' }
                    ]
                  )}
                </div>
              </div>

              {/* Environmental */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800 flex items-center">
                  <TestTube className="h-4 w-4 mr-2 text-green-600" />
                  Environmental
                </h4>
                
                <div className="space-y-3">
                  {/* Asbestos Type Control */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-medium">Asbestos Type</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {editingStatus ? (
                        <select
                          value={tempStatus.asbestos_type !== undefined ? tempStatus.asbestos_type : site.asbestos_type || 'none'}
                          onChange={(e) => setTempStatus(prev => ({ ...prev, asbestos_type: e.target.value }))}
                          className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="none">None</option>
                          <option value="friable">Friable</option>
                          <option value="non_friable">Non-Friable</option>
                          <option value="mixed">Mixed</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      ) : (
                        <>
                          <AlertTriangle className={`h-4 w-4 ${
                            site.asbestos_type === 'friable' ? 'text-red-600' :
                            site.asbestos_type === 'non_friable' ? 'text-orange-600' :
                            site.asbestos_type === 'mixed' ? 'text-blue-600' :
                            site.asbestos_type === 'unknown' ? 'text-purple-600' :
                            'text-green-600'
                          }`} />
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            site.asbestos_type === 'friable' ? 'bg-red-100 text-red-800' :
                            site.asbestos_type === 'non_friable' ? 'text-white' :
                            site.asbestos_type === 'mixed' ? 'bg-blue-100 text-blue-800' :
                            site.asbestos_type === 'unknown' ? 'bg-purple-100 text-purple-800' :
                            'bg-green-100 text-green-800'
                          }`} style={site.asbestos_type === 'non_friable' ? { backgroundColor: '#f59e0b' } : {}}>
                            {site.asbestos_type === 'non_friable' ? 'Non-Friable' :
                             site.asbestos_type === 'mixed' ? 'Mixed' :
                             site.asbestos_type === 'unknown' ? 'Unknown' :
                             site.asbestos_type?.charAt(0).toUpperCase() + site.asbestos_type?.slice(1) || 'None'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {renderStatusControl(
                    'air_monitoring',
                    'Air Monitoring',
                    <Wind className="h-4 w-4 text-blue-600" />,
                    [
                      { value: 'required', label: 'Required' },
                      { value: 'not_required', label: 'Not Required' }
                    ]
                  )}

                  {renderStatusControl(
                    'soil_testing',
                    'Soil Test Pits',
                    <TestTube className="h-4 w-4 text-green-600" />,
                    [
                      { value: 'required', label: 'Required' },
                      { value: 'not_required', label: 'Not Required' }
                    ]
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Hygienist Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Nominated Hygienist</h3>
              {editingStatus && (
                <button
                  onClick={() => setShowAddHygienist(true)}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add New</span>
                </button>
              )}
            </div>
            
            {editingStatus ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Hygienist</label>
                  <select
                    value={tempStatus.hygienist_id || projectStatus?.hygienist_id || ''}
                    onChange={(e) => handleHygienistChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">No hygienist required</option>
                    {hygienists.map(hygienist => (
                      <option key={hygienist.id} value={hygienist.id}>
                        {hygienist.name} - {hygienist.phone} {hygienist.company ? `(${hygienist.company})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                {showAddHygienist && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Add New Hygienist</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                          type="text"
                          value={newHygienist.name}
                          onChange={(e) => setNewHygienist(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Dr. John Smith"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                        <input
                          type="tel"
                          value={newHygienist.phone}
                          onChange={(e) => setNewHygienist(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="+61 2 9999 9999"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={newHygienist.email}
                          onChange={(e) => setNewHygienist(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="john@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <input
                          type="text"
                          value={newHygienist.company}
                          onChange={(e) => setNewHygienist(prev => ({ ...prev, company: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Hygiene Consulting Ltd"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-3 mt-4">
                      <button
                        onClick={handleAddHygienist}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Add Hygienist
                      </button>
                      <button
                        onClick={() => setShowAddHygienist(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {projectStatus?.hygienist_name ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">{projectStatus.hygienist_name}</p>
                        <div className="flex items-center space-x-2 text-sm text-blue-700">
                          <Phone className="h-3 w-3" />
                          <span>{projectStatus.hygienist_phone}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-500">No hygienist assigned to this project</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Site Photo */}
          {site.photo_url && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Site Photo</h3>
              <img
                src={site.photo_url}
                alt={site.name}
                className="w-full h-64 object-cover rounded-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Employee Time & Wages Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                <Clock className="h-5 w-5 text-gray-600" />
                <span>Employee Time & Wages</span>
              </h3>
              <Link
                to="/timesheets"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All Timesheets
              </Link>
            </div>

            {loadingTimesheetData ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : siteTimesheetData ? (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <p className="text-xs font-medium text-blue-900">Total Net Hours</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      {siteTimesheetData.totalHours.toFixed(1)}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">hours worked (excl. breaks)</p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <Calculator className="h-4 w-4 text-green-600" />
                      <p className="text-xs font-medium text-green-900">Total Wages</p>
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      ${siteTimesheetData.totalPay.toFixed(2)}
                    </p>
                    <p className="text-xs text-green-600 mt-1">labor costs</p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <Timer className="h-4 w-4 text-purple-600" />
                      <p className="text-xs font-medium text-purple-900">Regular Hours</p>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">
                      {siteTimesheetData.totalRegularHours.toFixed(1)}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">standard time</p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <Clock className="h-4 w-4 text-orange-600" />
                      <p className="text-xs font-medium text-orange-900">Overtime Hours</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-700">
                      {siteTimesheetData.totalOvertimeHours.toFixed(1)}
                    </p>
                    <p className="text-xs text-orange-600 mt-1">overtime</p>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">{siteTimesheetData.timesheetCount}</span> timesheet entries recorded for this site
                    </span>
                  </div>
                  {siteTimesheetData.totalHours > 0 && siteTimesheetData.totalPay > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-gray-600">Average Rate</p>
                      <p className="text-sm font-semibold text-gray-900">
                        ${(siteTimesheetData.totalPay / siteTimesheetData.totalHours).toFixed(2)}/hr
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No timesheet data available for this site</p>
              </div>
            )}
          </div>

          {/* Assigned Workers */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Workers with Inductions</h3>
              <button
                onClick={() => {
                  fetchAvailableWorkers();
                  setShowAssignWorkers(true);
                }}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Workers</span>
              </button>
            </div>
            {site.workers && site.workers.length > 0 ? (
              <div className="space-y-3">
                {site.workers.map((worker) => (
                  <div key={worker.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{worker.name}</p>
                        <p className="text-xs text-gray-500">{worker.role} • {worker.email}</p>
                        {/* Show induction status for this worker */}
                        {site.inductions && (
                          <div className="mt-1">
                            {site.inductions
                              .filter(induction => induction.worker?.id === worker.id)
                              .map(induction => (
                                <span 
                                  key={induction.id}
                                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                                    induction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    induction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {induction.status === 'completed' ? '✓ Inducted' :
                                   induction.status === 'pending' ? '⏳ Pending' :
                                   '⚠️ Expired'}
                                </span>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/workers/${worker.id}`}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        View Worker
                      </Link>
                      <button
                        onClick={() => handleRemoveWorker(worker.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No workers have inductions for this site yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-gray-700">Assigned Workers</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {site.workers?.length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <ClipboardCheck className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-700">Completed Inductions</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {site.inductions?.filter(i => i.status === 'completed').length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-gray-700">Pending Inductions</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {site.inductions?.filter(i => i.status === 'pending').length || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Inductions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Inductions</h3>
            {site.inductions && site.inductions.length > 0 ? (
              <div className="space-y-3">
                {site.inductions.slice(0, 5).map((induction) => (
                  <div key={induction.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getInductionStatusIcon(induction.status)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {induction.worker?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(induction.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/inductions/${induction.id}`}
                      className="text-orange-600 hover:text-orange-700 text-xs"
                    >
                      View
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No inductions recorded yet.</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/inductions"
                className="block w-full text-left p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center">
                  <ClipboardCheck className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">New Induction</p>
                    <p className="text-xs text-green-600">Process worker induction</p>
                  </div>
                </div>
              </Link>
              <Link
                to="/workers"
                className="block w-full text-left p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Assign Worker</p>
                    <p className="text-xs text-blue-600">Add worker to site</p>
                  </div>
                </div>
              </Link>
              <Link
                to="/reports"
                className="block w-full text-left p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">Generate Report</p>
                    <p className="text-xs text-purple-600">Site safety report</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
          {/* Estimated Waste Quantities */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Calculator className="h-5 w-5 mr-2 text-blue-600" />
                Estimated Waste Quantities
              </h3>
              <div className="flex items-center space-x-3">
                {editingWasteEstimates && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Units:</span>
                    <button
                      onClick={() => handleGlobalUnitChange('tonnes')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        globalUnit === 'tonnes' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Tonnes
                    </button>
                    <button
                      onClick={() => handleGlobalUnitChange('cubic_metres')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        globalUnit === 'cubic_metres' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      m³
                    </button>
                  </div>
                )}
                {editingWasteEstimates ? (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingWasteEstimates(false)}
                      className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center space-x-1"
                    >
                      <Check className="h-3 w-3" />
                      <span>Save</span>
                    </button>
                    <button
                      onClick={() => setEditingWasteEstimates(false)}
                      className="px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm flex items-center space-x-1"
                    >
                      <X className="h-3 w-3" />
                      <span>Cancel</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingWasteEstimates(true)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center space-x-1"
                  >
                    <Edit className="h-3 w-3" />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loadingWasteData ? (
                <div className="col-span-full flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading waste data...</span>
                </div>
              ) : (
              Object.entries(wasteEstimates).map(([key, value]) => (
                <div key={key} className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {key === 'demolition_waste' ? 'Demolition waste (General Rubbish)' : 
                     key === 'bricks_tile' ? 'Bricks & Tile' :
                     key.replace('_', ' ')}
                  </label>
                  <div className="flex items-center space-x-2">
                    {editingWasteEstimates ? (
                      <>
                        <input
                          type="number"
                          value={value.amount}
                          onChange={(e) => handleWasteEstimateChange(key, 'amount', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                          placeholder="0"
                          min="0"
                          step="0.1"
                        />
                        <select
                          value={value.unit}
                          onChange={(e) => handleWasteEstimateChange(key, 'unit', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm min-w-[80px]"
                        >
                          <option value="tonnes">tonnes</option>
                          <option value="cubic_metres">m³</option>
                        </select>
                      </>
                    ) : (
                      <div className="flex-1 px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-900 min-h-[38px] flex items-center">
                        <span className="font-medium">
                          {value.amount || '0'} {value.unit === 'cubic_metres' ? 'm³' : 'tonnes'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
              )}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total estimated waste:</span>
                <span className="font-medium text-gray-900">
                  <span className="text-xs text-gray-500">
                    (Mixed units - see individual estimates)
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <SiteForm
          site={site}
          onSubmit={handleUpdateSite}
          onClose={() => setShowEditModal(false)}
          loading={submitting}
        />
      )}

      {/* Assign Workers Modal */}
      {showAssignWorkers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Add Workers to Site</h3>
              </div>
              <button
                onClick={() => {
                  setShowAssignWorkers(false);
                  setSelectedWorkers([]);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {availableWorkers.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Select workers to create pending inductions for this site. Only workers without existing inductions are shown.
                  </p>
                  
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {availableWorkers.map((worker) => (
                      <div 
                        key={worker.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          selectedWorkers.includes(worker.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleWorkerSelection(worker.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{worker.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{worker.role.replace('_', ' ')} • {worker.email}</p>
                            {worker.certifications && worker.certifications.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {worker.certifications.slice(0, 2).map((cert, index) => (
                                  <span key={index} className="bg-green-100 text-green-800 px-1 py-0.5 rounded text-xs">
                                    {cert}
                                  </span>
                                ))}
                                {worker.certifications.length > 2 && (
                                  <span className="text-xs text-gray-500">+{worker.certifications.length - 2} more</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedWorkers.includes(worker.id)}
                            onChange={() => toggleWorkerSelection(worker.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      {selectedWorkers.length} worker{selectedWorkers.length !== 1 ? 's' : ''} selected
                    </p>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowAssignWorkers(false);
                          setSelectedWorkers([]);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAssignWorkers}
                        disabled={selectedWorkers.length === 0 || assigningWorkers}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {assigningWorkers ? 'Adding...' : `Add ${selectedWorkers.length} Worker${selectedWorkers.length !== 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Available Workers</h4>
                  <p className="text-gray-500 mb-4">
                    All active workers already have inductions for this site, or no workers have been registered yet.
                  </p>
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={() => {
                        setShowAssignWorkers(false);
                        setSelectedWorkers([]);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                    <Link
                      to="/workers"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add New Worker
                    </Link>
                  </div>
                </div>
              )}
              
              {site.quote_url && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Quote Spreadsheet Available</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(site.quote_url, '_blank')}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Download
                      </button>
                      <button
                        onClick={processSpreadsheet}
                        disabled={processingSpreadsheet}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {processingSpreadsheet ? 'Processing...' : 'Extract Quantities'}
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    Click "Extract Quantities" to automatically pull waste estimates from the uploaded spreadsheet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};